import type { ReactNode } from "react";

import { usePermissions } from "@/features/rbac/hooks/usePermissions";
import type { PermissionOrWildcard } from "@/features/rbac/permissions/catalog";

interface CanProps {
  /** Permiso o permisos requeridos. */
  permission: PermissionOrWildcard | readonly PermissionOrWildcard[];
  /** Con una lista: `any` exige uno cualquiera, `all` exige todos. */
  mode?: "any" | "all";
  /** Qué mostrar cuando no se concede. Por defecto, nada. */
  fallback?: ReactNode;
  children: ReactNode;
}

/**
 * Muestra sus hijos solo si la persona tiene el permiso.
 *
 * Mientras los permisos cargan no renderiza nada: es preferible un instante sin
 * botón a mostrar una acción que después desaparece.
 *
 * @example
 * ```tsx
 * <Can permission="users:delete">
 *   <DeleteUserButton user={user} />
 * </Can>
 *
 * <Can permission={["servers:infra_edit", "servers:hosts_manage"]} fallback={<ReadOnlyBadge />}>
 *   <EditInfraButton />
 * </Can>
 * ```
 */
export function Can({
  permission,
  mode = "any",
  fallback = null,
  children,
}: CanProps): React.JSX.Element | null {
  const { can, canAll, canAny, isLoading } = usePermissions();

  if (isLoading) return null;

  const list = Array.isArray(permission)
    ? (permission as readonly PermissionOrWildcard[])
    : null;

  const granted = list
    ? mode === "all"
      ? canAll(list)
      : canAny(list)
    : can(permission as PermissionOrWildcard);

  return <>{granted ? children : fallback}</>;
}
